import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {IMessage, IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {WB_EntityRow} from "../PA.Core/PAdbSchemaRows";


export interface IHierarchyNodeProvider {

    // Async, read from Source as needed, loads into cache
    //-- Find one
    findNodeByWbId(mapperName: string, wbId: number): Promise<INode>

    findNodeByKeySetId(mapperName: string, sKeySetId: string, sId: string): Promise<INode>

    //-- Find multiple
    findNodesByWbIds(mapperName: string, wbIds: number[]): Promise<INode[]>

    findNodesByKeySetIds(mapperName: string, sKeySetId: string, arrIds: string[]): Promise<INode[]>

    // Sync, pull from cache only
    findNodeByWbIdSync(mapperName: string, wbId: number): INode

    findNodeByKeySetIdSync(mapperName: string, keySetId: string, id: string): INode

    findNodeByLogicalKeySync(logicalKey: INodeLogicalKey): INode

    // async for MD Generated Hierarchy
    findNodeByLogicalKey(logicalKey: INodeLogicalKey): Promise<INode>
    findOrCreateLogicalKeyNode(logicalKey: INodeLogicalKey, options?: { sCreateId?: string, sCreateName?: string, dbContext?: PAdbContext }): Promise<INode> // assumes the node provider knows where it stores it.

    // EntityTypes
    readonly entityTypes: WB_EntityRow[]
    readonly bLoaded: boolean

    OnLoadComplete(): Promise<void>
}


export interface INode {

    // Key
    readonly wbId: number
    readonly provider: IHierarchyNodeProvider

    // Logical Identity
    readonly id_ui: string
    readonly key_set: string

    readonly bUseLogicalKey: boolean
    readonly logicalKey: INodeLogicalKey

    // Entity Typing
    /**
     * Returns true if this INode is of the given entity type Wb Id
     * @param wbEntityId The Wb ID of the entity's type (e.g., ProductGroupType, CustomerType, etc.)
     * @param opts Include optional sales area information to narrow possible types
     **/
    isEntityType(wbEntityId: number, opts?: { wbSalesAreaIds?: number[] }): boolean

    readonly entityTypes: WB_EntityRow[]

    // Storage, needed for Uniqueness w/ wbId
    readonly mapperName: string

    // Other properties
    readonly node_name: string

}

export interface INodeLogicalKeyComponent {
    keyId: string,
    dataType: string,
    valueWbId?: number,
    valueWbEntityId?: number,  // dataType=entity, wbId
    valueNumber?: number,                          // dataType=number
    valueString?: string,                          // dataType=string
    valueWbAttributeMId?: number
}

export interface INodeLogicalKey {
    [keyId: string]: INodeLogicalKeyComponent
}

export interface IHierarchyService {
    getHierarchyProcessingStatus(wbHierarchyId: number): Promise<IHierarchyBuilderOperationStatus>

    startHierarchyProcessing(wbHierarchyId: number, options: IHierarchyBuilderOperationStartOptions): Promise<void>

    getNodeWbIdByLKNode(wbMasterDataSetId: number, entityDimension: string, logicalKey: INodeLogicalKey): Promise<IGetNodeWbIdByLKNodeResponse>

    exportHierarchy(wbHierarchyId: number, maintenancePeriod: number, options: { [key: string]: any }): Promise<IExportHierarchyResponse>
}

export interface IGetNodeWbIdByLKNodeResponse {
    wbIds: number[],
    dupError?: boolean,
    errors?: string[]
}

export interface IExportHierarchyResponse {
    bSuccess: boolean,
    sErrors: string[],
    wbFileId: number,
    path: string
}


export enum HierarchyProcessingStatus {
    Idle = "Idle",
    NotStarted = "NotStarted",
    Starting = "Starting",
    Running = "Running",
    Stopping = "Stopping",
    Stopped = "Stopped",
    RunningWithErrors = "RunningErrors",
    CompletedWithErrors = "CompletedErrors",
    CompletedSuccess = "CompletedSuccess"
}

export interface IHierarchyBuilderOperationStatus {
    Status: HierarchyProcessingStatus,
    Options: IHierarchyBuilderOperationStartOptions,
    StartTime: Date,
    EndTime: Date,
    ProgressProviderChannelGUID: string,
    Messages: IMessage[]
}

export interface IHierarchyBuilderOperationStartOptions {

    dbContext?: PAdbContext
    ProgressProvider?: ProgressProvider
    MessageProvider?: MessageProvider

    bBuildWorkflow?: boolean     // Load, Build, Update
    bBuildSkipWrite?: boolean    // to simulate without writing, check for changes..

    // Other Additional Operations
    bExtractEntities?: boolean
    bExtractAttributes?: boolean
    bExportHierarchy?: boolean

    bWait?: boolean
}


export interface IHierarchyStructure {
    // Identity
    readonly wbHierarchyId: number    // WB_Hierarchy
    //in WB_Hierarchy, you can find the WB_MDSet_ID and the Entity_Dimension.

    readonly entityDimension: string     // Customer, Product, Period, SalesOrg
    //use the entity dimension to find the WB_Entity records, which are the levels/types of customers that are used in the hierarchy
    readonly wbId: number             // WB_CUSTH_ID, WB_PRDH_ID, etc.
    readonly id_ui: string
    readonly name: string

    readonly bHasEffectiveDates: boolean
    readonly bHasClassificationNodes: boolean
    readonly bHasEnterpriseEntities: boolean

    readonly dbContext: PAdbContext


    // Loading mechanism
    readonly LoadProgressProvider: IProgressProvider
    readonly LoadMessageProvider: IMessageProvider

    OnLoadComplete(): Promise<void>

    readonly bLoaded: boolean

    // Common Properties
    readonly Nodes: IHierarchyNodeProvider
    readonly Relations: IHierarchyRelationship[]

    createRelation(): Promise<IHierarchyRelationship>

    readonly bHasLevels: boolean
    readonly Levels: IHierarchyStructureLevel[]

    createLevel(levelId?: string): Promise<IHierarchyStructureLevel>

    readonly bHasMaintenancePeriods: boolean
    readonly MaintenancePeriods?: IHierarchyMaintenancePeriod[]

    readonly bHasSalesAreas: boolean
    readonly SalesAreas?: IHierarchyStructureSalesArea[]

    createSalesArea?(wbSalesAreaId?: number): Promise<IHierarchyStructureSalesArea>

    readonly bHasStatus: boolean
    readonly Status?: string
    setStatus?(statusId: string): void

    readonly bHasDeletedNodes: boolean
    readonly DeletedNodes?: INode[]

    // Operations
    readonly ValidateMessageProvider: IMessageProvider

    Validate(options?: IHierarchyStructureValidateOptions): Promise<void>

    readonly SaveProgressProvider: IProgressProvider
    readonly SaveMessageProvider: IMessageProvider

    Save(options?: IHierarchyStructureSaveOptions): Promise<void>

    readonly bSaved: boolean

    readonly DeleteMessageProvider: IMessageProvider
    Delete(): Promise<void>
    readonly bDeleted: boolean
}

/**
 *
 * Builds a cached hierarchy for the given IHierarchyStructure and maintenance period
 *
 **/
export interface IHierarchyStructureNavigator {
    readonly hierarchyStructure: IHierarchyStructure
    readonly effectiveStartDt: Date
    readonly effectiveEndDt: Date

    readonly LoadMessageProvider: IMessageProvider
    readonly LoadProgressProvider: IProgressProvider

    onLoadComplete(): Promise<void>

    readonly bLoaded: boolean


    // Basic API, All hierarchies
    getTopLevelNodes(): INode[]            // Top level hierarchy nodes
    getChildNodes(aNode: INode): INode[]   // Children of a node in the hierarchy
    getParent(aNode: INode): INode          // Parent of a node
    getLeafNodes(): INode[]                // Nodes at Lowest depth
    getLeafNodesOfAnyLevel(): INode[]      // Nodes that do not have children, regardless of them being at the bottom of the hierarchy (i.e., will return ragged-branch leaves)
    getRelationshipByChild(aNode: INode): IHierarchyRelationship

    getDepth(aNode: INode, bImplicit?: boolean): number

    getNodesAtDepth(depth: number): INode[]  // Nodes from explicit top node

    // Basic API for Hierarchies with Known Levels
    getChildNodesOfLevel(aNode: INode, levelId: string): INode[] // All children at the hierarchy level
    getParentNodeOfLevel(aNode: INode, levelId: string): INode    // Parent of node, at the given level
    getNodesOfLevel(levelId: string): INode[]

    getLevelByLevelDepth(levelDepth: number): IHierarchyStructureLevel

    // API for Typed Hierarchies
    getNodesOfType(wbEntityId: number): INode[]                     // All nodes in the hierarchy that are of the given type
    getChildNodesOfType(aNode: INode, wbEntityId: number): INode[] // All children of the node of the given type
    getParentNodeOfType(aNode: INode, wbEntityId: number): INode   // All parents of the given child node in the hierarchy that are of the given type


    // Diagnostic API
    getParentNodesNotConnectedToTopLevel(): INode[]  // All nodes that do not have a parent node and are not top-level nodes. These are disconnected branches
    getAllNodesNotConnectedToTopLevel(): INode[]     // All nodes that do not have a connection to any top-level nodes


}

export interface IHierarchyStructureNavigatorLoadOptions {
    maxDepth?: number,
    messageProvider?: MessageProvider,
    progressProvider?: ProgressProvider
}

export interface IHierarchyStructureLevel {
    readonly hierarchy: IHierarchyStructure

    readonly levelId: string
    readonly levelName: string
    readonly levelDepth: number

    // Operations
    setLevelId(id: string): void

    setLevelName(name: string): void

    setLevelDepth(depth: number): void

    // Typing and Uniqueness, comes from Harness
    readonly levelUnique: boolean
    readonly levelEntityTypeId: number
    readonly levelEntityAttributeId: number
    readonly levelEnterpriseEntityTypeId: number

    // Typing Operations
    setLevelUnique(levelUnique: boolean): void

    setLevelEntityType(wbEntityId: number): void
    setLevelEntityAttribute(wbAttributeMId: number): void

    setLevelEnterpriseEntityType(wbEntityId: number): void

    // Storage Properties
    readonly isActive: boolean
    readonly isActiveNew: boolean
    readonly isModified: boolean
    readonly original?: IHierarchyStructureLevel

    // Storage Operations
    SaveBatch(): Promise<void>

    Delete(): void

}

export interface IHierarchyStructureSalesArea {
    readonly hierarchy: IHierarchyStructure

    readonly wbSalesAreaId: number

    // Typing Operations
    setWbSalesAreaId(wbSalesAreaId: number): void

    // Storage Properties
    readonly isActive: boolean
    readonly isActiveNew: boolean
    readonly isModified: boolean
    readonly original?: IHierarchyStructureSalesArea

    // Storage Operations
    SaveBatch(): Promise<void>

    Delete(): void

}

export interface IHierarchyMaintenancePeriod {
    readonly periodId: number
    readonly periodName: string
    readonly startDt: Date
    readonly endDt: Date
    readonly active: boolean
}


export interface IHierarchyStructureSaveOptions {
    bSkipValidate?: boolean,
    validateOptions?: IHierarchyStructureValidateOptions

    [id: string]: any
}

export interface IHierarchyStructureValidateOptions {
    [id: string]: any
}


export interface IHierarchyRelationship {
    readonly hierarchy: IHierarchyStructure

    readonly child: INode
    readonly parent: INode
    // Optional properties for Effective Dated Hierarchies
    readonly effectiveStartDt?: Date
    readonly effectiveEndDt?: Date

    // Storage Properties
    readonly isActive: boolean
    readonly isActiveNew: boolean
    readonly isModified: boolean
    readonly original?: IHierarchyRelationship

    // Operations
    setChild(aNode: INode): void

    setParent(aNode: INode): void

    setEffectiveStartDt(aDate: Date): void

    setEffectiveEndDt(aDate: Date): void

    // Storage Operations
    SaveBatch(): Promise<void>

    Delete(): void

    // TODO: add optional properties for delta,
    readonly rowWB_Id?: number
    readonly rowSrcId?: string
    readonly rowStatus?: string
    readonly rowModifiedDt?: Date

}



